// src/pages/AnalyticsAlt.tsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  orderBy,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebase";

import { Model } from "survey-core";

// Tabulator: import the class you can `new` and the type separately
import { TabulatorFull as Tabulator } from "tabulator-tables";
import type { Tabulator as TabulatorType } from "tabulator-tables";
import "survey-analytics/survey.analytics.tabulator.css";
import "tabulator-tables/dist/css/tabulator.css";
import "tabulator-tables/dist/css/tabulator_simple.min.css";
import "./analyticsAltOverrides.css";

import { Box, Typography, Button, TextField, Stack } from "@mui/material";
import PageHeader from "../components/PageHeader";

export default function AnalyticsAlt() {
  const { id } = useParams<{ id: string }>();
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<TabulatorType | null>(null);

  const [surveyDef, setSurveyDef] = useState<any>(null);
  const [responses, setResponses] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);

  const fetchResponses = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not authenticated");

      const respSnap = await getDocs(
        query(
          collection(db, "surveys", id!, "responses"),
          orderBy("submittedAt", "asc")
        )
      );

      const rows = respSnap.docs
        .map((docSnap) => {
          const data = docSnap.data() as any;
          if (!showDeleted && data?.deleted === true) return null;
          const answers = data?.answers ? { ...data.answers } : {};
          Object.keys(answers).forEach((key) => {
            if (
              answers[key] === "other" &&
              typeof answers[`${key}-Comment`] === "string" &&
              answers[`${key}-Comment`].trim() !== ""
            ) {
              answers[key] = answers[`${key}-Comment`];
            }
          });
          return { _id: docSnap.id, _deleted: data?.deleted === true, ...answers };
        })
        .filter(Boolean);

      setResponses(rows);
    } catch (e: any) {
      console.error("Error fetching responses:", e);
      setError(e.message);
    }
  }, [id, showDeleted]);

  useEffect(() => {
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) throw new Error("Not authenticated");

        const snap = await getDoc(doc(db, "surveys", id!));
        const data = snap.data() as any;
        if (!snap.exists()) {
          throw new Error("Survey not found or access denied");
        }
        setSurveyDef(data.json);
        await fetchResponses();
      } catch (e: any) {
        console.error(e);
        setError(e.message);
      }
    })();
  }, [id, fetchResponses]);

  const handleDeleteResponse = useCallback(
    async (responseId: string) => {
      if (!id) return;
      if (
        window.confirm(
          "Are you sure you want to delete this response? This action cannot be undone."
        )
      ) {
        try {
          const user = auth.currentUser;
          await updateDoc(doc(db, "surveys", id, "responses", responseId), {
            deleted: true,
            deletedAt: serverTimestamp(),
            deletedBy: user?.uid || null,
          });
          await fetchResponses();
        } catch (e: any) {
          console.error("Error deleting response:", e);
          setError(`Failed to delete response: ${e.message}`);
        }
      }
    },
    [id, fetchResponses]
  );

  const handleRestoreResponse = useCallback(
    async (responseId: string) => {
      if (!id) return;
      try {
        await updateDoc(doc(db, "surveys", id, "responses", responseId), {
          deleted: false,
          deletedAt: null,
          deletedBy: null,
        });
        await fetchResponses();
      } catch (e: any) {
        console.error("Error restoring response:", e);
        setError(`Failed to restore response: ${e.message}`);
      }
    },
    [id, fetchResponses]
  );

  useEffect(() => {
    if (!surveyDef || !containerRef.current) return;

    if (tableRef.current) {
      tableRef.current.destroy();
      tableRef.current = null;
    }

    const survey = new Model(surveyDef);

    const shorten = (label: string) => (label && label.length > 35 ? `${label.slice(0, 35)}…` : label);

    const questionColumns: any[] = survey.getAllQuestions().map((q: any) => ({
      field: q.name as string,
      title: shorten((q.title || q.name) as string),
      headerFilter: "input",
      headerFilterParams: { valuesLookup: false },
      formatter: (cell: any) => {
        const v = cell.getValue();
        if (Array.isArray(v)) {
          return v
            .map((item) => (item && typeof item === "object" ? Object.values(item).join(", ") : String(item)))
            .join(", ");
        }
        if (v && typeof v === "object") {
          return Object.values(v).join(", ");
        }
        return String(v ?? "");
      },
      sorter: "string",
      minWidth: 220,
      headerSort: true,
      headerHozAlign: "left",
      headerWordWrap: true,
    }));

    const actionsColumn: any = {
      title: "Actions",
      field: "_actions",
      width: 80,
      hozAlign: "center",
      frozen: true,
      headerSort: false,
      formatter: (cell: any) => {
        const rowData = cell.getRow().getData();
        if (rowData?._deleted) {
          return `<span class="delete-action delete-action-pill restore">Restore</span>`;
        }
        return `<span class="delete-action delete-action-pill">Delete</span>`;
      },
      cellClick: (e: any, cell: any) => {
        if ((e.target as HTMLElement).classList.contains("delete-action")) {
          const rowData = cell.getRow().getData();
          if (rowData?._deleted) {
            handleRestoreResponse(rowData._id);
          } else {
            handleDeleteResponse(rowData._id);
          }
        }
      },
    };

    const columns: any[] = [actionsColumn];
    for (const col of questionColumns) columns.push(col);

    const table = new Tabulator(containerRef.current!, {
      data: responses,
      columns,
      layout: "fitDataFill",
      reactiveData: true,
      pagination: true,
      paginationMode: "local",
      paginationSize: 25,
      movableColumns: true,
      placeholder: "No responses yet",
      columnHeaderVertAlign: "bottom",
      movableRows: false,
      responsiveLayout: false,
    });
    tableRef.current = table;

    if (globalFilter.trim()) {
      table.setFilter((data: any) => {
        const needle = globalFilter.toLowerCase();
        return Object.keys(data).some((k) =>
          String(data[k] ?? "").toLowerCase().includes(needle)
        );
      });
    }

    return () => {
      table.destroy();
      tableRef.current = null;
    };
  }, [surveyDef, responses, handleDeleteResponse, handleRestoreResponse, globalFilter]);

  const handleDownloadCSV = () => {
    const filename = `${surveyDef?.title || id}-results.csv`;
    tableRef.current?.download("csv", filename);
  };

  const handleGlobalSearch = (value: string) => {
    setGlobalFilter(value);
    if (!tableRef.current) return;
    if (!value.trim()) {
      tableRef.current.clearFilter(true);
      return;
    }
    tableRef.current.setFilter((data: any) => {
      const needle = value.toLowerCase();
      return Object.keys(data).some((k) =>
        String(data[k] ?? "").toLowerCase().includes(needle)
      );
    });
  };

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  if (!surveyDef) {
    return (
      <Box p={4}>
        <Typography>Loading analytics…</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        width: "100%",
        pt: 4,
        px: "2vw",
      }}
    >
      <PageHeader
        title={`Results for “${surveyDef.title || id}”`}
        subtitle={`${responses.length} ${responses.length === 1 ? "response" : "responses"}`}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleDownloadCSV}
          sx={{
            fontWeight: 700,
            px: 2.5,
            borderRadius: "999px",
          }}
        >
          CSV
        </Button>
        <TextField
          size="small"
          placeholder="Search all columns…"
          value={globalFilter}
          onChange={(e) => handleGlobalSearch(e.target.value)}
          sx={{
            minWidth: 220,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#fff",
            },
          }}
        />
        <Button
          variant={showDeleted ? "contained" : "outlined"}
          color={showDeleted ? "secondary" : "inherit"}
          onClick={() => setShowDeleted((prev) => !prev)}
        >
          {showDeleted ? "Hide deleted" : "Show deleted"}
        </Button>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      />
    </Box>
  );
}
